// Team of the Week — shared pure helpers (TP_TOTW_BRIEF).
//
// Everything here is deterministic and unit-testable: rating formatting
// (API delivers an INTEGER x100 — 760 = 7.60), the brand-blue rating tiers
// (lead decision: blue family only, NO red/green/amber), the verbatim round
// label rule, the pitch coordinate projection (vertical pitch, GK at the
// bottom) and the design's collision spreader.

import { extractDetailedPositions } from "../../utils/positionFormatter";

// ── rating ─────────────────────────────────────────────────────────────────

// 760 -> "7.6", 1000 -> "10.0". ALWAYS one decimal (brief hard rule; the
// self-QA greps the DOM for three-digit ratings).
export const formatRating = (rating) => {
  if (rating == null || rating === "") return null;
  const value = Number(rating);
  if (!Number.isFinite(value)) return null;
  return (value / 100).toFixed(1);
};

// Handoff tier() scale VERBATIM (TOTWDesktop.dc.html:343 — Marin 2026-08-14:
// the handoff wins over the brief's "brand-blue only" line). Ratings arrive
// ×100 from the API, the handoff thresholds are on the /100 scale.
export const ratingTier = (rating) => {
  const value = (Number(rating) || 0) / 100;
  if (value >= 9) return { bg: "#2283FF", fg: "#FFFFFF", solid: "#2283FF" };
  if (value >= 8) return { bg: "#0E9F6E", fg: "#FFFFFF", solid: "#0E9F6E" };
  if (value >= 7) return { bg: "#84E1BC", fg: "#03543F", solid: "#0E9F6E" };
  if (value >= 6) return { bg: "#E3A008", fg: "#FFFFFF", solid: "#E3A008" };
  if (value >= 5) return { bg: "#F35525", fg: "#FFFFFF", solid: "#F35525" };
  return { bg: "#E02424", fg: "#FFFFFF", solid: "#E02424" };
};

// Matches/Lineup build (Part D): the same tier scale for values that are
// already on the /10 decimal scale (e.g. lineup "rating": "7.2", match-rail
// ratings 6.77). ONE threshold source — this only rescales and delegates.
export const ratingTier10 = (v) => ratingTier(Number(v) * 100);

// "7.2" / 6.77 -> "7.2" — always ONE decimal (tabular-nums display).
// null/undefined/"" /NaN -> null, caller renders nothing.
// 0 / negative -> null too: the provider ships rating "0.0" for players
// without a rated appearance (unused subs, thin legacy rows), and the
// handoff tier scale renders NO chip for !(n > 0) — a red "0.0" badge on
// the match rail is a data artefact, not a rating (QA 2026-08-17).
export const formatRating10 = (v) => {
  if (v == null || v === "") return null;
  const value = Number(v);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value.toFixed(1);
};

// ── round label ────────────────────────────────────────────────────────────

// Marin 2026-08-14 (league-only V1): numeric upstream names render as
// "Round {n}" (translated via the static bundle's template pattern);
// anything non-numeric is shown VERBATIM — no data normalisation.
export const roundLabel = (round) => {
  const name = round?.name != null ? String(round.name).trim() : "";
  const numeric = name !== "" && /^\d+$/.test(name)
    ? name
    : (round?.round != null ? String(round.round) : null);
  if (name !== "" && !/^\d+$/.test(name)) return name; // verbatim
  if (numeric != null) return `Round ${numeric}`;
  return name || "";
};

// ── position groups (list view) ────────────────────────────────────────────

export const POSITION_GROUPS = [
  { key: "G", label: "Goalkeeper" },
  { key: "D", label: "Defence" },
  { key: "M", label: "Midfield" },
  { key: "F", label: "Attack" },
];

// Group the (1..11) players by their lineup position (brief data rule 5:
// the LINEUP position groups the list, never the profile position). The BE
// already derives missing positions from location_y, so `position` is
// authoritative; anything still unknown lands in Midfield rather than
// vanishing (no player is ever dropped from the list).
export const groupPlayers = (players = []) =>
  POSITION_GROUPS.map((group) => ({
    ...group,
    players: players.filter((p) =>
      group.key === "M"
        ? p.position === "M" || !["G", "D", "F"].includes(p.position)
        : p.position === group.key,
    ),
  })).filter((group) => group.players.length > 0);

// Detailed position (Marin finding 2: "Wenn wir detaillierte Position
// liefern können, liefern wir detaillierte Position"). The BE ships the
// platform's detailed-position dict (`position_detail`, same shape as
// tp_detailed_position everywhere else — see utils/positionFormatter.js);
// this splits it into { primary, others[] } for the popover's
// main-bold / side-muted rendering. Null -> caller falls back to the
// coarse lineup line. Codes are normalised uppercase and deduped.
export const positionDetailParts = (detail) => {
  const extracted = extractDetailedPositions(detail);
  if (!extracted) return null;
  const norm = (code) =>
    code && typeof code === "string" ? code.trim().toUpperCase() : null;
  const primary = norm(extracted.primary_position || extracted.primaryPosition);
  if (!primary) return null;
  const others = [
    extracted.secondary_position || extracted.secondaryPosition,
    extracted.teritory_position ||
      extracted.tertiary_position ||
      extracted.tertiaryPosition,
  ]
    .map(norm)
    .filter(Boolean)
    .filter((code, index, list) => code !== primary && list.indexOf(code) === index);
  return { primary, others };
};

// Standout of the round = highest rating (first hit wins on ties).
export const standoutOf = (players = []) =>
  players.reduce(
    (best, p) => (best == null || (p.rating || 0) > (best.rating || 0) ? p : best),
    null,
  );

// ── pitch projection ───────────────────────────────────────────────────────

// The design's collision spreader: nudges overlapping cards apart on the
// axis with the smaller overlap, clamped to the safe area. W/H are the card
// footprint in % units, min/max the clamping box.
export const spread = (pts, W, H, minL, maxL, minT, maxT) => {
  const a = pts.map((p) => ({ ...p }));
  for (let it = 0; it < 80; it += 1) {
    let moved = false;
    for (let i = 0; i < a.length; i += 1) {
      for (let j = i + 1; j < a.length; j += 1) {
        const dx = a[j].left - a[i].left;
        const dy = a[j].top - a[i].top;
        const ox = W - Math.abs(dx);
        const oy = H - Math.abs(dy);
        if (ox > 0 && oy > 0) {
          moved = true;
          if (oy / H <= ox / W) {
            const s = (oy / 2 + 0.25) * (dy >= 0 ? 1 : -1);
            a[i].top -= s;
            a[j].top += s;
          } else {
            const s = (ox / 2 + 0.25) * (dx >= 0 ? 1 : -1);
            a[i].left -= s;
            a[j].left += s;
          }
        }
      }
    }
    for (const p of a) {
      p.left = Math.min(maxL, Math.max(minL, p.left));
      p.top = Math.min(maxT, Math.max(minT, p.top));
    }
    if (!moved) break;
  }
  return a;
};

// Project location_x/y (0-100, low y = own goal) onto the VERTICAL pitch
// (GK at the bottom) as % positions, then spread collisions. Constants are
// the design's (desktop / mobile differ slightly in margins + card size).
export const placePlayers = (players = [], variant = "desktop") => {
  const cfg =
    variant === "mobile"
      ? { x0: 5, xs: 0.9, y0: 7, ys: 0.89, W: 16, H: 14, minL: 10, maxL: 90, minT: 8, maxT: 92 }
      : { x0: 8, xs: 0.84, y0: 6, ys: 0.88, W: 18, H: 12, minL: 11, maxL: 89, minT: 7, maxT: 93 };
  const pts = players.map((p, index) => ({
    id: p.ts_player_id || String(index),
    left: cfg.x0 + (p.location_x ?? 50) * cfg.xs,
    top: cfg.y0 + (100 - (p.location_y ?? 50)) * cfg.ys,
  }));
  const placed = spread(pts, cfg.W, cfg.H, cfg.minL, cfg.maxL, cfg.minT, cfg.maxT);
  const at = Object.fromEntries(placed.map((q) => [q.id, q]));
  return players.map((p, index) => {
    const key = p.ts_player_id || String(index);
    return { ...p, _left: at[key].left, _top: at[key].top };
  });
};

// Initials for the avatar fallback ("no n/a anywhere" — brief data rule 1).
export const playerInitials = (player) => {
  const source = player?.name || player?.short_name || "";
  const initials = source
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return initials || "?";
};

// Default skeleton slots (a 4-3-3) so the loading pitch matches the frames
// even before any lineup was ever loaded.
export const SKELETON_SLOTS = [
  { location_x: 50, location_y: 6 },
  { location_x: 85, location_y: 24 },
  { location_x: 63, location_y: 19 },
  { location_x: 37, location_y: 19 },
  { location_x: 15, location_y: 24 },
  { location_x: 76, location_y: 47 },
  { location_x: 50, location_y: 40 },
  { location_x: 24, location_y: 47 },
  { location_x: 82, location_y: 74 },
  { location_x: 50, location_y: 82 },
  { location_x: 18, location_y: 74 },
].map((slot, index) => ({ ...slot, ts_player_id: `skeleton-${index}` }));
